/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

/**
 * Provider for looking up Form-Conversion ManifestSchemas associated with a
 * ReportNamedInstance via the ReportNamedInstanceManifestSchemaJoin table.
 *
 * @class FormConversionProvider
 * @extends PictProvider
 */
class FormConversionProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		/**
		 * Cached Form-Conversion schemas keyed by IDReportNamedInstance.
		 * @type {Object.<number, Array>}
		 */
		this._SchemaCache = {};
	}

	/**
	 * Get Form-Conversion ManifestSchemas for a ReportNamedInstance.
	 *
	 * 1. Queries ReportNamedInstanceManifestSchemaJoins filtered by IDReportNamedInstance
	 * 2. Collects IDManifestSchema values from the join records
	 * 3. Batch-fetches ManifestSchemas with FBL~IDManifestSchema~INN~{ids}~FBV~Type~EQ~Form-Conversion
	 *
	 * @param {number} pIDReportNamedInstance
	 * @returns {Promise<Array>} Array of ManifestSchema records with Type = 'Form-Conversion'
	 */
	async getFormConversionSchemas(pIDReportNamedInstance)
	{
		if (!pIDReportNamedInstance)
		{
			return [];
		}

		// Check cache first
		if (this._SchemaCache[pIDReportNamedInstance])
		{
			return this._SchemaCache[pIDReportNamedInstance];
		}

		// Step 1: Get join records
		const tmpJoinRecords = await new Promise((pResolve, pReject) =>
		{
			this.fable.HeadlightRestClient.getJSON(
				`ReportNamedInstanceManifestSchemaJoins/FilteredTo/FBV~IDReportNamedInstance~EQ~${pIDReportNamedInstance}/0/100`,
				(pError, pBody) =>
				{
					if (pError)
					{
						this.log.error('Error fetching ReportNamedInstanceManifestSchemaJoins:', pError);
						return pReject(pError);
					}
					return pResolve(pBody || []);
				});
		});

		if (!tmpJoinRecords.length)
		{
			this._SchemaCache[pIDReportNamedInstance] = [];
			return [];
		}

		// Step 2: Collect ManifestSchema IDs
		const tmpManifestSchemaIDs = tmpJoinRecords
			.map((pJoin) => pJoin.IDManifestSchema)
			.filter((pID) => pID && pID > 0);

		if (!tmpManifestSchemaIDs.length)
		{
			this._SchemaCache[pIDReportNamedInstance] = [];
			return [];
		}

		// Step 3: Batch-fetch ManifestSchemas filtered to Form-Conversion type
		const tmpIDList = tmpManifestSchemaIDs.join(',');
		const tmpSchemas = await new Promise((pResolve, pReject) =>
		{
			this.fable.HeadlightRestClient.getJSON(
				`ManifestSchemas/FilteredTo/FBL~IDManifestSchema~INN~${tmpIDList}~FBV~Type~EQ~Form-Conversion/0/100`,
				(pError, pBody) =>
				{
					if (pError)
					{
						this.log.error('Error fetching ManifestSchemas for Form-Conversion:', pError);
						return pReject(pError);
					}
					return pResolve(pBody || []);
				});
		});

		// Enrich schemas with file type info from their template Artifacts
		const tmpArtifactIDs = tmpSchemas
			.map((pSchema) => pSchema.IDArtifact)
			.filter((pID) => pID && pID > 0);

		if (tmpArtifactIDs.length > 0)
		{
			const tmpArtifactIDList = [...new Set(tmpArtifactIDs)].join(',');
			const tmpArtifacts = await new Promise((pResolve, pReject) =>
			{
				this.fable.HeadlightRestClient.getJSON(
					`Artifacts/FilteredTo/FBL~IDArtifact~INN~${tmpArtifactIDList}/0/100`,
					(pError, pBody) =>
					{
						if (pError)
						{
							this.log.warn('Error fetching Artifacts for Form-Conversion schemas:', pError);
							return pResolve([]);
						}
						return pResolve(pBody || []);
					});
			});

			// Build a lookup map by IDArtifact
			const tmpArtifactMap = {};
			for (const tmpArtifact of tmpArtifacts)
			{
				tmpArtifactMap[tmpArtifact.IDArtifact] = tmpArtifact;
			}

			// Resolve file type for each schema from its Artifact
			for (const tmpSchema of tmpSchemas)
			{
				const tmpArtifact = tmpArtifactMap[tmpSchema.IDArtifact];
				if (tmpArtifact)
				{
					let tmpDatum = tmpArtifact.Datum;
					if (typeof tmpDatum === 'string')
					{
						try { tmpDatum = JSON.parse(tmpDatum); }
						catch (pErr) { tmpDatum = {}; }
					}

					// Resolve mime type from Datum.FileMimeType, with fallback to FileName extension
					let tmpMimeType = tmpDatum?.FileMimeType || '';
					if (!tmpMimeType && tmpDatum?.FileName)
					{
						const tmpExtMatch = tmpDatum.FileName.match(/\.([^.]+)$/);
						const tmpExt = tmpExtMatch ? tmpExtMatch[1].toLowerCase() : '';
						if (tmpExt === 'pdf') { tmpMimeType = 'application/pdf'; }
						else if (tmpExt === 'xlsx') { tmpMimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'; }
						else if (tmpExt === 'xls') { tmpMimeType = 'application/vnd.ms-excel'; }
					}

					tmpSchema.TemplateMimeType = tmpMimeType;
				}
			}
		}

		this._SchemaCache[pIDReportNamedInstance] = tmpSchemas;
		return tmpSchemas;
	}

	/**
	 * Resolve the IDReportNamedInstance from a DocumentType hash and IDProject,
	 * then load associated Form-Conversion schemas.
	 *
	 * Uses the ReportNamedInstancesByProject endpoint to find the matching RNI,
	 * then delegates to getFormConversionSchemas().
	 *
	 * @param {string} pDocumentType - The DocumentType hash (e.g. 'MI-CAG-Walbec')
	 * @param {number} pIDProject - The project ID (from ReportData.IDProject)
	 * @returns {Promise<Array>} Array of ManifestSchema records with Title for display
	 */
	async getFormConversionSchemasForDocument(pDocumentType, pIDProject)
	{
		if (!pDocumentType || !pIDProject)
		{
			return [];
		}

		// Look up RNIs for this project
		const tmpRNIs = await new Promise((pResolve, pReject) =>
		{
			this.fable.HeadlightRestClient.getJSON(
				`ReportNamedInstancesByProject/${pIDProject}/0/250`,
				(pError, pBody) =>
				{
					if (pError)
					{
						this.log.error('Error fetching ReportNamedInstances for project:', pError);
						return pReject(pError);
					}
					return pResolve(pBody || []);
				});
		});

		// Find the RNI matching the document type hash
		const tmpMatchingRNI = tmpRNIs.find((pRNI) => pRNI.Hash === pDocumentType);
		if (!tmpMatchingRNI)
		{
			this.log.warn(`No ReportNamedInstance found with Hash '${pDocumentType}' for project ${pIDProject}`);
			return [];
		}

		return this.getFormConversionSchemas(tmpMatchingRNI.IDReportNamedInstance);
	}
}

module.exports = FormConversionProvider;
